import { parseBoolelanOrNull, parseIntOrNull } from 'src/app/+shared/helpers/parsers.helper';
import {
  CcdDrrDevicesFormModel,
  DrrCcdDeviceDto,
  DeviceFormModel,
  DeviceUnitDto,
  DeviceUnitFormModel,
  DeviceWrapperFormModel,
  DrrCcdDeviceApplicationDto,
  FileFormModel,
  SimpleDocumentDto,
  RegistrationUnitDto,
  RegistrationUnitFormModel,
  NetworkDisturbancesDataFormModel,
  NetworkDisturbancesDataDto,
} from './technical-data.domain';
import { toDateOnlyString } from 'src/app/+shared/helpers/date.helper';

export async function mapFormToDrrCcdDeviceApplicationDto(form): Promise<DrrCcdDeviceApplicationDto> {
  const drrCcdDeviceApplicationDto = new DrrCcdDeviceApplicationDto();
  drrCcdDeviceApplicationDto.is_registration_device = form.type === 'drr';
  drrCcdDeviceApplicationDto.is_consumption_device = form.type === 'ccd';
  drrCcdDeviceApplicationDto.network_connection_point_power = form.simultaneouslyRequiredPower;
  drrCcdDeviceApplicationDto.application_id = form.applicationId ?? 0;
  drrCcdDeviceApplicationDto.is_commissioning_done = form.is_commissioning_done;
  drrCcdDeviceApplicationDto.is_commissioning_confirmed = form.is_commissioning_confirmed;
  drrCcdDeviceApplicationDto.commissioning_date = toDateOnlyString(form.commissioning_date);
  drrCcdDeviceApplicationDto.commissioning_document = await mapFormToDocumentDto(form.drr_ccd_commissioning);
  drrCcdDeviceApplicationDto.drr_ccd_devices = await Promise.all(
    form.devices.map(async d => await mapFormToDrrCcdDevice(d.device, form.applicationId))
  );

  return drrCcdDeviceApplicationDto;

  async function mapFormToDrrCcdDevice(device: DeviceFormModel, applicationId: any): Promise<DrrCcdDeviceDto> {
    const drrCcdDeviceDto = new DrrCcdDeviceDto();
    drrCcdDeviceDto.id = device.id;
    drrCcdDeviceDto.device_type = device.ccdType;
    drrCcdDeviceDto.pump_type = device.heatingPumpType;
    drrCcdDeviceDto.air_conditioning_type = device.airConditioningType;
    drrCcdDeviceDto.wall_box_type = device.wallBoxType;
    drrCcdDeviceDto.other_type = device.other;
    drrCcdDeviceDto.total_grid_power_consumption = device.totalConsumption;
    drrCcdDeviceDto.type_of_control = device.typeOfControl;
    drrCcdDeviceDto.module_choice = device.module;
    drrCcdDeviceDto.is_cascade_measurement = device.cascadeMeasurementDesired ?? false;
    drrCcdDeviceDto.is_separate_meter_required = device.separateMeterRequired ?? false;
    drrCcdDeviceDto.meter_number = device.meterNumber;
    drrCcdDeviceDto.meter2 = device.meter2;
    drrCcdDeviceDto.drr_ccd_device_application_id = applicationId ?? 0;

    drrCcdDeviceDto.device_units = await Promise.all(device.deviceUnits?.units?.map(async u => await mapFormToDeviceUnit(u, null)));

    return drrCcdDeviceDto;
  }

  async function mapFormToDeviceUnit(unitFormValue: DeviceUnitFormModel, parentId: any): Promise<DeviceUnitDto> {
    const deviceUnit = {} as DeviceUnitDto;
    deviceUnit.id = unitFormValue.id;
    deviceUnit.manufacturer = unitFormValue.manufacturer;
    deviceUnit.designation_type = unitFormValue.designationType;
    deviceUnit.power = unitFormValue.electricalPower;
    deviceUnit.heater_power = unitFormValue.powerOfAdditionalHeater;
    deviceUnit.has_additional_heating = unitFormValue.hasAdditionalHeating;
    deviceUnit.coupling_type = unitFormValue.couplingType;
    deviceUnit.has_load_management = unitFormValue.loadManagementAvailable;
    deviceUnit.load_management_type = unitFormValue.loadManagementType;
    deviceUnit.parent_device_id = parentId;
    deviceUnit.drr_ccd_device_id = unitFormValue.drrCcdDeviceId;
    deviceUnit.unavailable = unitFormValue.unavailable ?? false;
    deviceUnit.storage_not_in_parallel_mode = unitFormValue.storageNotInParallelMode;
    deviceUnit.data_sheet_document = await mapFormToDocumentDto(unitFormValue.dataSheet);
    deviceUnit.data_sheet_wr_document = await mapFormToDocumentDto(unitFormValue.dataSheetWr);
    deviceUnit.circuit_diagram_document = await mapFormToDocumentDto(unitFormValue.circuitDiagramDocument);
    deviceUnit.registration_unit = await mapFormToRegistrationUnitDto(unitFormValue.registrationUnit);

    deviceUnit.children_devices = unitFormValue.subUnits
      ? await Promise.all(unitFormValue.subUnits.map(su => mapFormToDeviceUnit(su, unitFormValue.id)))
      : [];

    return deviceUnit;
  }
}

export function mapDrrCcdDeviceApplicationDtoToForm(dto: DrrCcdDeviceApplicationDto): CcdDrrDevicesFormModel {
  let formValue: CcdDrrDevicesFormModel = {
    applicationId: dto.application_id ? parseInt(dto.application_id.toString()) : null,
    type: dto.is_registration_device ? 'drr' : 'ccd',
    simultaneouslyRequiredPower: dto.network_connection_point_power ? dto.network_connection_point_power : null,
    devices: dto.drr_ccd_devices.map(mapConsumptionDeviceToForm),
    commissioningDate: dto.commissioning_date ? new Date(dto.commissioning_date) : null,
    isCommissioningDone: dto.is_commissioning_done,
    isCommissioningConfirmed: dto.is_commissioning_confirmed,
    commissioningDocumentId: dto.commissioning_document_id,
    commissioningDocument: mapDocumentDtoToForm(dto.commissioning_document),
  };

  return formValue;
}

function mapConsumptionDeviceToForm(dto: DrrCcdDeviceDto): DeviceWrapperFormModel {
  return {
    device: {
      id: parseIntOrNull(dto.id),
      ccdType: dto.device_type,
      heatingPumpType: dto.pump_type,
      airConditioningType: dto.air_conditioning_type,
      wallBoxType: dto.wall_box_type,
      other: dto.other_type,
      totalConsumption: dto.total_grid_power_consumption,
      typeOfControl: dto.type_of_control,
      module: dto.module_choice,
      cascadeMeasurementDesired: JSON.parse(dto.is_cascade_measurement?.toString()),
      separateMeterRequired: JSON.parse(dto.is_separate_meter_required?.toString()),
      meterNumber: dto.meter_number,
      meter2: dto.meter2,
      deviceUnits: {
        units: dto.device_units.map(mapDeviceUnitToForm),
      },
    },
  };
}
function mapDeviceUnitToForm(dto: DeviceUnitDto): DeviceUnitFormModel {
  return {
    id: parseIntOrNull(dto.id),
    manufacturer: dto.manufacturer,
    designationType: dto.designation_type,
    electricalPower: dto.power,
    powerOfAdditionalHeater: dto.heater_power,
    hasAdditionalHeating: JSON.parse(dto.has_additional_heating?.toString() ?? null),
    couplingType: dto.coupling_type,
    loadManagementAvailable: JSON.parse(dto.has_load_management?.toString() ?? null),
    loadManagementType: dto.load_management_type,
    drrCcdDeviceId: dto.drr_ccd_device_id,
    subUnits: dto.children_devices?.map(mapDeviceSubUnitToForm),
    unavailable: JSON.parse(dto.unavailable?.toString() ?? null),
    storageNotInParallelMode: JSON.parse(dto.storage_not_in_parallel_mode?.toString() ?? null),
    dataSheet: mapDocumentDtoToForm(dto.data_sheet_document),
    dataSheetWr: mapDocumentDtoToForm(dto.data_sheet_wr_document),
    circuitDiagramDocument: mapDocumentDtoToForm(dto.circuit_diagram_document),
    registrationUnit: mapRegistrationUnitDtoToForm(dto.registration_unit),
  };
}

function mapDeviceSubUnitToForm(dto: DeviceUnitDto): DeviceUnitFormModel {
  return {
    manufacturer: dto.manufacturer,
    designationType: dto.designation_type,
    electricalPower: dto.power,
    powerOfAdditionalHeater: dto.heater_power,
    hasAdditionalHeating: JSON.parse(dto.has_additional_heating?.toString() ?? null),
    couplingType: dto.coupling_type,
    loadManagementType: dto.load_management_type,
    unavailable: JSON.parse(dto.unavailable?.toString() ?? null),
    id: parseIntOrNull(dto.id),
    drrCcdDeviceId: dto.drr_ccd_device_id,
    loadManagementAvailable: dto.has_load_management,
    dataSheet: mapDocumentDtoToForm(dto.data_sheet_document),
  };
}

function mapDocumentDtoToForm(dto: SimpleDocumentDto): FileFormModel {
  if (!dto) return null;

  return {
    id: dto.document_id,
    name: dto.name,
    file: null,
    contentType: dto.content_type,
  };
}

async function mapFormToDocumentDto(fileFormModel: FileFormModel): Promise<SimpleDocumentDto> {
  if (!fileFormModel || !fileFormModel.name) return null;

  return {
    document_id: fileFormModel?.id,
    name: fileFormModel?.name,
    document_type: fileFormModel.documentType,
    content_type: fileFormModel.contentType,
    content: await convertFileToBase64(fileFormModel?.file),
  };
}

async function mapFormToRegistrationUnitDto(form: RegistrationUnitFormModel): Promise<RegistrationUnitDto> {
  if (!form || !form.deviceName) return null;
  return {
    id: form.id ?? 0,
    device_unit_id: form.deviceUnitId,
    device_name: form.deviceName,
    device_number: form.deviceNumber,
    heating_type: form.heatingType,
    sk_measuring_concept: form.skMeasuringConcept,
    single_device_type: form.singleDeviceType,
    is_emergency_generator_not_in_parallel: form.isEmergencyGeneratorNotInParallel,
    is_energy_from_emergency_generator_not_remunerated: form.isEnergyFromEmergencyGeneratorNotRemunerated,
    is_power_provided_from_customer_system: form.isPowerProvidedFromCustomerSystem,
    is_system_installation_vde_arn4100: form.isSystemInstallationVdeArn4100,
    is_network_disturbance_complied_with_vde_arn4100: form.isNetworkDisturbanceCompliedWithVdeArn4100,
    is_input_of_customer_system_less_or_equal75: form.isInputOfCustomerSystemLessOrEqual75,
    mains_interference_device_type: form.mainsInterferenceDeviceType,
    connection_type: form.connectionType,
    data_collection_document: await mapFormToDocumentDto(form.dataCollectionDocument),
    site_plan_installation_document: await mapFormToDocumentDto(form.sitePlanInstallationDocument),
    network_disturbances_data: mapFormToNetworkDisturbancesDataDto(form.mainsInterferenceDeviceType ? form.networkDisturbancesData : null),
    network_disturbances_data_id: form.networkDisturbancesData?.id,
  };
}

function mapFormToNetworkDisturbancesDataDto(form: NetworkDisturbancesDataFormModel): NetworkDisturbancesDataDto {
  if (!form) return null;

  return {
    id: form.id ?? 0,
    registration_unit_id: form.registrationUnitId,
    is_asynchronous_motor: form.isAsynchronousMotor,
    is_drive_with_power_converter: form.isDriveWithPowerConverter,
    rated_power: form.ratedPower,
    rated_voltage: form.ratedVoltage,
    rated_speed: form.ratedSpeed,
    rated_current: form.ratedCurrent,
    power_factor: form.powerFactor,
    efficiency: form.efficiency,
    ratio_starting_current_rated: form.ratioStartingCurrentRated,
    start_up_type: form.startUpType,
    start_up_circuit: form.startUpCircuit,
    other_startup_circuit: form.otherStartupCircuit,
    load_number_rotation_changes: form.loadNumberRotationChanges,
    attempts_per_hour: form.attemptsPerHour,
    attempts_per_day: form.attemptsPerDay,
    max_welding_performance: form.maxWeldingPerformance,
    weld_number: form.weldNumber,
    weld_duration: form.weldDuration,
    xray_rated_power: form.xrayRatedPower,
    xray_required_power: form.xrayRequiredPower,
    power_converter_efficiency: form.powerConverterEfficiency,
    max_number_of_recordings: form.maxNumberOfRecordings,
    commutation_inductance: form.commutationInductance,
    converter_transformer: form.converterTransformer,
    rectifier_design: form.rectifierDesign,
    pulse_number: form.pulseNumber,
    switching_group: form.switchingGroup,
    power_converter_type: form.powerConverterType,
    relative_voltage: form.relativeVoltage,
    center_point_circuit_type: form.centerPointCircuitType,
    max_inrush_current: form.maxInrushCurrent,
    reactive_power_range: form.reactivePowerRange,
    reactive_power_per_stage: form.reactivePowerPerStage,
    number_of_steps: form.numberOfSteps,
    resonance_frequency: form.resonanceFrequency,
  };
}

function mapRegistrationUnitDtoToForm(dto: RegistrationUnitDto): RegistrationUnitFormModel {
  if (!dto)
    return {
      id: null,
      deviceUnitId: null,
      deviceName: null,
      deviceNumber: null,
      heatingType: null,
      skMeasuringConcept: null,
      singleDeviceType: null,
    };

  return {
    id: parseIntOrNull(dto.id),
    deviceUnitId: parseIntOrNull(dto.device_unit_id),
    deviceName: dto.device_name,
    deviceNumber: dto.device_number,
    heatingType: dto.heating_type,
    skMeasuringConcept: dto.sk_measuring_concept,
    singleDeviceType: dto.single_device_type,
    isEmergencyGeneratorNotInParallel: dto.is_emergency_generator_not_in_parallel,
    isEnergyFromEmergencyGeneratorNotRemunerated: dto.is_energy_from_emergency_generator_not_remunerated,
    isPowerProvidedFromCustomerSystem: dto.is_power_provided_from_customer_system,
    isSystemInstallationVdeArn4100: dto.is_system_installation_vde_arn4100,
    isNetworkDisturbanceCompliedWithVdeArn4100: parseBoolelanOrNull(dto.is_network_disturbance_complied_with_vde_arn4100),
    isInputOfCustomerSystemLessOrEqual75: parseBoolelanOrNull(dto.is_input_of_customer_system_less_or_equal75),
    mainsInterferenceDeviceType: dto.mains_interference_device_type,
    connectionType: dto.connection_type,
    dataCollectionDocument: mapDocumentDtoToForm(dto.data_collection_document),
    sitePlanInstallationDocument: mapDocumentDtoToForm(dto.site_plan_installation_document),
    networkDisturbancesData: mapNetworkDisturbancesDataDtoToForm(dto.network_disturbances_data),
  };
}

function mapNetworkDisturbancesDataDtoToForm(dto: NetworkDisturbancesDataDto): NetworkDisturbancesDataFormModel {
  if (!dto) return { id: null, registrationUnitId: null };

  return {
    id: parseIntOrNull(dto.id),
    registrationUnitId: parseIntOrNull(dto.registration_unit_id),
    isAsynchronousMotor: dto.is_asynchronous_motor,
    isDriveWithPowerConverter: dto.is_drive_with_power_converter,
    ratedPower: dto.rated_power,
    ratedVoltage: dto.rated_voltage,
    ratedSpeed: dto.rated_speed,
    ratedCurrent: dto.rated_current,
    powerFactor: dto.power_factor,
    efficiency: dto.efficiency,
    ratioStartingCurrentRated: dto.ratio_starting_current_rated,
    startUpType: dto.start_up_type,
    startUpCircuit: dto.start_up_circuit,
    otherStartupCircuit: dto.other_startup_circuit,
    loadNumberRotationChanges: dto.load_number_rotation_changes,
    attemptsPerHour: dto.attempts_per_hour,
    attemptsPerDay: dto.attempts_per_day,
    maxWeldingPerformance: dto.max_welding_performance,
    weldNumber: dto.weld_number,
    weldDuration: dto.weld_duration,
    xrayRatedPower: dto.xray_rated_power,
    xrayRequiredPower: dto.xray_required_power,
    powerConverterEfficiency: dto.power_converter_efficiency,
    maxNumberOfRecordings: dto.max_number_of_recordings,
    commutationInductance: dto.commutation_inductance,
    converterTransformer: dto.converter_transformer,
    rectifierDesign: dto.rectifier_design,
    pulseNumber: dto.pulse_number,
    switchingGroup: dto.switching_group,
    powerConverterType: dto.power_converter_type,
    relativeVoltage: dto.relative_voltage,
    centerPointCircuitType: dto.center_point_circuit_type,
    maxInrushCurrent: dto.max_inrush_current,
    reactivePowerRange: dto.reactive_power_range,
    reactivePowerPerStage: dto.reactive_power_per_stage,
    numberOfSteps: dto.number_of_steps,
    resonanceFrequency: dto.resonance_frequency,
  };
}

function convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file); // Reads file as Base64
    reader.onload = () => resolve(reader.result?.toString().split(',')[1] || ''); // Remove "data:*/*;base64,"
    reader.onerror = error => reject(error);
  });
}
